import { useOrg } from '@client/hooks/business/org/useOrg';
import { useUser } from '@client/hooks/business/user/useUser';
import { useAllFeatureFlags } from '@client/hooks/useFeatureFlag';
import type { FileRouteTypes } from '@client/routeTree.gen';
import { isDeferredRoutePath } from '@client/utils/routing/deferred-routes';
import type { Role, UserFlag } from '@shared/constants/business/user';
import type { AppModule } from '@shared/constants/system/app';
import type { Permission } from '@shared/permissions/permissions';
import { hasConsoleWorkspaceAccess, hasPermission } from '@shared/permissions/roles';
import type { StaticDataRouteOption } from '@tanstack/react-router';
import { useCallback } from 'react';
import { useDeferredRoutesLoaded } from './useDeferredRoutesLoaded';
import { useGetRouteMetadata } from './useGetRouteMetadata';

type AccessUser = ReturnType<typeof useUser> | undefined;
type AccessOrg = ReturnType<typeof useOrg> | undefined;

/**
 * Checks if user has employee access for the route.
 * `org:admin` and `super:admin` intentionally bypass `restrictToEmployee` (confirmed 2026-07-09).
 */
function hasEmployeeAccess(user: AccessUser, restrictToEmployee: boolean | undefined) {
    if (user?.isEmployee || hasPermission(user?.roles ?? [], 'org:admin')) {
        return true;
    }

    if (hasPermission(user?.roles ?? [], 'super:admin')) {
        return true;
    }

    return !restrictToEmployee;
}

/**
 * Checks if user has required roles for the route
 */
function hasRequiredRoles(user: AccessUser, requiredRoles: Role[] | undefined) {
    return !requiredRoles?.length || (user && requiredRoles.some((item) => user.roles.includes(item)));
}

/**
 * Checks if user has required flags for the route
 */
function hasRequiredUserFlags(user: AccessUser, requiredFlags: UserFlag[] | undefined) {
    return !requiredFlags?.length || (user && requiredFlags.some((item) => user.flags.includes(item)));
}

/**
 * Checks if organization has required modules enabled
 */
function hasRequiredModules(org: AccessOrg, requiredModules: AppModule[] | undefined) {
    return !requiredModules?.length || (org && requiredModules.some((item) => org.enabledModules.includes(item)));
}

// OR logic: user needs ANY ONE of the listed permissions (not all).
// This allows routes to list multiple permissions where any grants access,
// e.g. permissions: ['admin_orgs:read', 'admin_orgs:update'] — either suffices.
function hasRequiredPermissions(user: AccessUser, requiredPermissions: Permission[] | undefined) {
    return (
        !requiredPermissions?.length || (user && requiredPermissions.some((item) => hasPermission(user.roles, item)))
    );
}

/** Console-only routes are closed to users whose roles carry no console workspace (CUR-1049). */
function hasRequiredConsoleWorkspace(user: AccessUser, consoleWorkspace: boolean | undefined) {
    return !consoleWorkspace || (user !== undefined && hasConsoleWorkspaceAccess(user.roles));
}

function hasRequiredFeatureFlag(flags: Record<string, unknown>, requiredFeatureFlag: string | undefined) {
    if (!requiredFeatureFlag) return true;
    return Boolean(flags[requiredFeatureFlag]);
}

/**
 * Outcome of the route access check.
 *
 * `blocked-by-feature-flag` is split out from `denied` because it is the one dimension that can
 * still change after mount: user, org and the route table are final once loaded, but the flag
 * map starts out empty (fresh browser) or incomplete (server bootstrap replaced the persisted
 * flags — CUR-891) until PostHog has evaluated flags. Callers that act destructively on a
 * denial (`RouteAccessGuard`) treat it as unknown until `useAreFeatureFlagsSettled` says the
 * current value is final; `denied` is final immediately.
 */
export type RouteAccess = 'granted' | 'denied' | 'blocked-by-feature-flag';

/**
 * Returns a function resolving already-merged route metadata to its {@link RouteAccess}. Stable
 * while the user, organisation and feature flags are unchanged.
 */
export function useGetRouteMetadataAccess() {
    const org = useOrg({ throwOnUndefined: false });
    const user = useUser({ throwOnUndefined: false });
    const flags = useAllFeatureFlags();

    return useCallback(
        (routeMetadata: StaticDataRouteOption): RouteAccess => {
            const passesNonFlagChecks = Boolean(
                hasEmployeeAccess(user, routeMetadata.restrictToEmployee) &&
                    hasRequiredRoles(user, routeMetadata.roles) &&
                    hasRequiredUserFlags(user, routeMetadata.userFlags) &&
                    hasRequiredModules(org, routeMetadata.modules) &&
                    hasRequiredPermissions(user, routeMetadata.permissions) &&
                    hasRequiredConsoleWorkspace(user, routeMetadata.consoleWorkspace),
            );
            if (!passesNonFlagChecks) {
                return 'denied';
            }

            return hasRequiredFeatureFlag(flags, routeMetadata.featureFlag) ? 'granted' : 'blocked-by-feature-flag';
        },
        [user, org, flags],
    );
}

/**
 * Returns a function resolving a route path to its {@link RouteAccess}. Stable while the user,
 * organisation, feature flags and route table are unchanged.
 */
export function useGetRouteAccess() {
    const getRouteMetadata = useGetRouteMetadata();
    const getRouteMetadataAccess = useGetRouteMetadataAccess();
    const deferredRoutesLoaded = useDeferredRoutesLoaded();

    return useCallback(
        (path: FileRouteTypes['to']): RouteAccess => {
            // A deferred area's routes carry its access rules. Until they are in the route table
            // the path matches only the root route, which restricts nothing, so answer no rather
            // than grant by omission. The answer is recomputed when the routes load.
            if (!deferredRoutesLoaded && isDeferredRoutePath(path)) {
                return 'denied';
            }

            const routeMetadata = getRouteMetadata(path);

            if (!routeMetadata) {
                return 'denied';
            }

            return getRouteMetadataAccess(routeMetadata);
        },
        [getRouteMetadata, getRouteMetadataAccess, deferredRoutesLoaded],
    );
}

/**
 * Returns a predicate for "may this user reach this route". The predicate is
 * stable while the user, organisation, feature flags and route table are
 * unchanged, so callers (e.g. the left navigation) can memoise whole-tree
 * access filtering against it.
 */
export function useHasRouteAccess() {
    const getRouteAccess = useGetRouteAccess();

    return useCallback((path: FileRouteTypes['to']): boolean => getRouteAccess(path) === 'granted', [getRouteAccess]);
}
